import * as PhosphorIcons from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import TableHeader from '../../components/generic/TableHeader';
import TestAnalysisLoader from '../../components/loaders/TestAnalysisLoader';
import { Tab, TabsContainer } from '../../utils/SubjectTabs';
import AccuracyChart from './charts/AccuracyChart';
import ComparativeHourwiseChart from './charts/ComparativeHourwiseChart';
import QualityOfTimeChart from './charts/QualityOfTimeChart';
import QuestionsAttemptedChart from './charts/QuestionsAttemptedChart';
import TimeJourneyChart from './charts/TimeJourneyChart';
import TimeSpentChart from './charts/TimeSpentChart';
import ComparativeHourWiseTable from './tables/ComparativeHourWiseTable';
import ComparativeTimeBreakdownTable from './tables/ComparativeTimeBreakdownTable';
import TimeBreakdownTable from './tables/TimeBreakdownTable';
import TimeJourneyTable from './tables/TimeJourneyTable';

export default function TimeAnalysis({ isComparative, comparativeData, timeAnalysisData, totalTime }) {
  const [subjectMeta, setSubjectMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  console.log('comparativeData in TimeAnalysis:', timeAnalysisData);

  const subjectwiseTabs = [
    { title: 'Time Spent', icon: 'TimerIcon', color: 'var(--q3-neutral-default)' },
    { title: 'Questions Attempted', icon: 'QuestionIcon', color: 'var(--q3-neutral-default)' },
    { title: 'Accuracy', icon: 'TargetIcon', color: 'var(--q3-neutral-default)' },
  ];

  const hourWiseTabs = [
    { title: 'Overall', icon: 'ChecksIcon', color: 'var(--q3-neutral-default)' },
    { title: 'Correct', icon: 'CheckIcon', color: 'var(--q3-green-normal)' },
    { title: 'Incorrect', icon: 'XIcon', color: 'var(--q3-danger-normal)' },
  ];

  const [selectedSubjectwiseTab, setSelectedSubjectwiseTab] = useState(subjectwiseTabs[0]);
  const [selectedQualitySubject, setSelectedQualitySubject] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedHourwiseTab, setSelectedHourwiseTab] = useState(hourWiseTabs[0]);
  const [selectedGraphicalHourwiseTab, setSelectedGraphicalHourwiseTab] = useState(hourWiseTabs[1]);

  const renderIcon = (icon, color) => {
    if (icon && PhosphorIcons[icon]) {
      return React.createElement(PhosphorIcons[icon], {
        size: 24,
        color: color || 'var(--q3-neutral-default)',
      });
    }
    return null;
  };

  useEffect(() => {
    if (isComparative || !timeAnalysisData) return;

    setIsLoading(true);

    setTimeout(() => {
      const response = timeAnalysisData.data; // Adjust as per your JSON

      setSubjectMeta(response.subjectMeta);
      setSelectedQualitySubject(Object.values(response.subjectMeta)[0]);
      setData(response);

      setIsLoading(false);
    }, 300);
  }, [isComparative, timeAnalysisData]);

  useEffect(() => {
    if (!isComparative || !comparativeData) return;

    let meta = structuredClone(comparativeData.subjectMeta);

    if (Object.keys(meta).length > 2) {
      const overall = { Overall: meta['Overall'] };
      delete meta['Overall'];
      meta = { ...overall, ...meta };
    } else {
      delete meta['Overall'];
    }

    setSubjectMeta(meta);
    setSelectedSubject(Object.values(meta)[0]);
  }, [isComparative, comparativeData]);

  if (isLoading) return <TestAnalysisLoader />;

  if (isComparative && comparativeData && subjectMeta)
    return (
      <div className="space-y-8">
        <TableHeader headerTitle={'Time Breakdown'} headerTitleClass={'border-b-0'}>
          <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
            <TabsContainer color={selectedSubject?.color}>
              {Object.values(subjectMeta).map((tab) => (
                <Tab
                  key={tab._id}
                  isActive={selectedSubject?.title === tab?.title}
                  color={selectedSubject?.title === tab?.title ? selectedSubject?.color : 'var(--q3-neutral-light)'}
                  tabColor={tab?.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => setSelectedSubject(tab)}
                >
                  {tab?.icon && <img src={tab?.icon} alt={tab?.title} />}
                  <p>{tab?.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>

          <ComparativeTimeBreakdownTable comparativeData={comparativeData} selectedSubject={selectedSubject} />
        </TableHeader>

        {/* Hourwise Breakdown */}
        {comparativeData?.comparativeData?.hour_wise_breakdown?.length > 0 && (
          <div className="space-y-8">
            <TableHeader headerTitle={'Hour Wise Breakdown'} headerTitleClass={'border-b-0'}>
              <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
                <TabsContainer color={selectedHourwiseTab?.color}>
                  {hourWiseTabs.map((tab, index) => (
                    <Tab
                      key={index}
                      isActive={selectedHourwiseTab?.title === tab?.title}
                      color={
                        selectedHourwiseTab?.title === tab?.title
                          ? selectedHourwiseTab?.color
                          : 'var(--q3-neutral-light)'
                      }
                      tabColor={tab?.color}
                      bgColor="var(--q3-surface-dim)"
                      onClick={() => setSelectedHourwiseTab(tab)}
                    >
                      {renderIcon(tab.icon, tab.color)}
                      <p>{tab?.title}</p>
                    </Tab>
                  ))}
                </TabsContainer>
              </div>

              <ComparativeHourWiseTable comparativeData={comparativeData} selectedHourWiseTab={selectedHourwiseTab} />
            </TableHeader>

            {/* Graphical */}
            <TableHeader headerTitle={'Graphical Hour-Wise Breakdown'} headerTitleClass={'border-b-0'}>
              <div className="w-full overflow-x-auto border border-[var(--q3-stroke-light)] border-y-0">
                <TabsContainer color={selectedGraphicalHourwiseTab?.color}>
                  {hourWiseTabs
                    .filter((tab) => tab.title !== 'Overall')
                    .map((tab, index) => (
                      <Tab
                        key={index}
                        isActive={selectedGraphicalHourwiseTab?.title === tab?.title}
                        color={
                          selectedGraphicalHourwiseTab?.title === tab?.title
                            ? selectedGraphicalHourwiseTab?.color
                            : 'var(--q3-neutral-light)'
                        }
                        tabColor={tab.color}
                        bgColor="var(--q3-surface-dim)"
                        onClick={() => setSelectedGraphicalHourwiseTab(tab)}
                      >
                        {renderIcon(tab.icon, tab.color)}
                        <p>{tab?.title}</p>
                      </Tab>
                    ))}
                </TabsContainer>
              </div>

              <ComparativeHourwiseChart
                comparativeData={comparativeData}
                selectedHourWiseTab={selectedGraphicalHourwiseTab}
              />
            </TableHeader>
          </div>
        )}
      </div>
    );

  // ----------------------------------------------------------
  // 🔵 NON-COMPARATIVE MOCK DATA UI
  // ----------------------------------------------------------
  return (
    <div className="space-y-8">
      <TableHeader headerTitle="Time Breakdown">
        <TimeBreakdownTable subjectMeta={subjectMeta} data={data?.timeAndAccuracy} />
      </TableHeader>

      <TableHeader headerTitle={'Subject-wise'} headerTitleClass={'border-b-0'}>
        <div className="border border-y-0 border-[var(--q3-stroke-light)]">
          <div className="w-full overflow-x-auto border border-y-0 border-[var(--q3-stroke-light)]">
            <TabsContainer color={selectedSubjectwiseTab?.color}>
              {subjectwiseTabs.map((tab) => (
                <Tab
                  key={tab.title}
                  isActive={selectedSubjectwiseTab?.title === tab.title}
                  color={
                    selectedSubjectwiseTab?.title === tab.title
                      ? selectedSubjectwiseTab?.color
                      : 'var(--q3-neutral-light)'
                  }
                  tabColor={tab.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => setSelectedSubjectwiseTab(tab)}
                >
                  {renderIcon(tab.icon)}
                  <p>{tab.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          </div>

          <div className="flex flex-row gap-6 py-4 px-6">
            {subjectMeta &&
              Object.values(subjectMeta)
                .filter((s) => s.title !== 'Overall')
                .map((subject) => (
                  <div key={subject.title} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: subject.color }} />
                    <p>{subject.title}</p>
                  </div>
                ))}
          </div>
        </div>

        {selectedSubjectwiseTab.title === 'Time Spent' ? (
          <TimeSpentChart timeData={data?.timeAndAccuracy} />
        ) : selectedSubjectwiseTab.title === 'Questions Attempted' ? (
          <QuestionsAttemptedChart questionsData={data?.timeAndAccuracy} />
        ) : selectedSubjectwiseTab.title === 'Accuracy' ? (
          <AccuracyChart accuracyData={data?.timeAndAccuracy} />
        ) : null}
      </TableHeader>

      <TableHeader headerTitle="Quality of Time Spent" headerTitleClass={'border-b-0'}>
        <div className="border border-y-0 border-[var(--q3-stroke-light)]">
          {subjectMeta && Object.values(subjectMeta).length > 2 && (
            <TabsContainer color={selectedQualitySubject?.color}>
              {Object.values(subjectMeta).map((tab) => (
                <Tab
                  key={tab.title}
                  isActive={selectedQualitySubject?.title === tab.title}
                  color={
                    selectedQualitySubject?.title === tab.title
                      ? selectedQualitySubject?.color
                      : 'var(--q3-neutral-light)'
                  }
                  tabColor={tab.color}
                  bgColor="var(--q3-surface-dim)"
                  onClick={() => setSelectedQualitySubject(tab)}
                >
                  <p>{tab.title}</p>
                </Tab>
              ))}
            </TabsContainer>
          )}
        </div>

        <QualityOfTimeChart qualityOfTime={data?.qualityOfTime} selectedSubject={selectedQualitySubject} />
      </TableHeader>

      <TableHeader headerTitle="Time Journey">
        <TimeJourneyTable timeJourney={data?.timeJourney} />
      </TableHeader>

      <TableHeader
        headerTitle={`Graphical View of Attempts Over ${
          totalTime >= 60 ? `${totalTime / 60} Hour(s)` : `${totalTime} Minutes`
        }`}
      >
        <TimeJourneyChart timeJourney={data?.timeJourney} />
      </TableHeader>
    </div>
  );
}
